/**
 * Return the site path with the current environment
 *
 * @description  Had to move this function out of sitePath.ts because tests would fail not being
 * able to read getConfig()
 */
export function sitePath_withEnv({
  path,
  lang,
  siteEnv,
}: {
  path: string;
  lang?: string;
  siteEnv: string;
}) {
  const langString = lang ? `/${lang}` : '';

  if (siteEnv === 'dev') {
    return `https://dev.terminal.io${langString + path}`;
  } else {
    return `https://www.terminal.io${langString + path}`;
  }
}

/**
 * Finds the key in an object that corresponds to a specific value
 * @param obj The object to search in
 * @param value The value to search for
 * @returns The key if found, undefined otherwise
 */
export function getKeyByValue<T extends Record<string, any>, V extends T[keyof T]>(
  obj: T,
  value: V,
): keyof T | undefined {
  return Object.keys(obj).find((key) => obj[key] === value) as keyof T | undefined;
}

export function capitalizeFirstLetter(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Normalizes a link string to ensure it's either an absolute URL (starting with 'http' or 'https')
 * or a root-relative path (starting with '/').
 * Useful for handling links from sources like Storyblok where internal links might be
 * represented without a leading slash.
 * It also removes the specific suffix `/[...slug]` if present at the end of the path.
 *
 * - Absolute URLs are returned unchanged.
 * - Root-relative paths are returned unchanged unless they end with `/[...slug]`.
 * - Other paths are assumed to be internal, have a leading '/' prepended, and are checked for the suffix.
 * - If the link ends with `/[...slug]`, this part is removed. If this results in an empty string, `/` is returned.
 *
 * @param link - The link string to normalize. Can be an absolute URL or a relative path.
 * @returns A normalized link string (either absolute URL or root-relative path, without the `/[...slug]` suffix).
 *
 * @example
 * // Absolute URLs remain unchanged
 * normalizeLink('https://example.com') // => 'https://example.com'
 * normalizeLink('http://example.com/path') // => 'http://example.com/path'
 * normalizeLink('https://example.com/pamp/[...slug]') // => 'https://example.com/pamp/[...slug]' (Absolute URLs are not modified)
 *
 * @example
 * // Root-relative paths remain unchanged (unless they have the suffix)
 * normalizeLink('/internal/page') // => '/internal/page'
 * normalizeLink('/') // => '/'
 *
 * @example
 * // Relative paths get a leading slash
 * normalizeLink('internal/page') // => '/internal/page'
 * normalizeLink('another') // => '/another'
 *
 * @example
 * // Suffix removal
 * normalizeLink('pamp/[...slug]') // => '/pamp'
 * normalizeLink('/pamp/[...slug]') // => '/pamp'
 * normalizeLink('/[...slug]') // => '/'
 * normalizeLink('abc/[...slug]/def') // => '/abc/[...slug]/def' (Suffix only removed at the very end)
 *
 * @example
 * // Edge cases
 * normalizeLink('') // => '/'
 */
export function normalizeLink(link: string): string {
  // Return absolute URLs immediately, without modification
  if (link.startsWith('http') || link.startsWith('https')) {
    return link;
  }

  // Ensure the link is root-relative if it's not absolute
  let normalizedLink = link.startsWith('/') ? link : `/${link}`;

  // Check and remove the specific suffix /[...slug]
  const slugSuffix = '/[...slug]';
  if (normalizedLink.endsWith(slugSuffix)) {
    // Remove the suffix
    normalizedLink = normalizedLink.slice(0, -slugSuffix.length);
    // If removing the suffix results in an empty string (original was just '/[...slug]'),
    // return '/' to represent the root path.
    if (normalizedLink === '') {
      return '/';
    }
  }

  return normalizedLink;
}
